export const TIER_LABELS = { tier1: "Tier 1", tier2: "Tier 2", tier3: "Tier 3" };
export const TIER_COLORS = {
  tier1: "border-emerald-700 text-emerald-400",
  tier2: "border-amber-700 text-amber-400",
  tier3: "border-[#D5001C] text-[#ff3b52]",
};
export const CERT_LABELS = {
  retail_demo: "Retail Demo",
  gt_demo: "GT Demo",
  west_gt_demo: "West Track Demo",
  gt_drift_demo: "GT Drift Demo",
  demo_lead: "Demo Lead",
  demo_lead_host: "Demo Lead Host",
  west_certified: "West Certified",
  corporate_lead: "Corporate Lead",
  off_road: "Off Road",
  cayenne_offroad: "Cayenne Off-Road",
  // Phase 15u — Accelerated premium program cert (private West-only 4-hour).
  accelerated: "Accelerated",
  // Phase PP-15 (Feb 2026) — PDC Trainer cert (PP-11). Gates PDC training
  // trainer assignment via `PdcTrainerPickerPopover`.
  pdc_trainer: "PDC Trainer",
};
export const CERT_COLORS = {
  retail_demo: "text-white",
  gt_demo: "text-lime-400",
  west_gt_demo: "text-purple-400",
  gt_drift_demo: "text-orange-400",
  demo_lead: "text-blue-400",
  demo_lead_host: "text-blue-400",
  west_certified: "text-yellow-400",
  corporate_lead: "text-pink-400",
  off_road: "text-zinc-300",
  cayenne_offroad: "text-[#b5835a]",
  // Phase 15u — teal-400 (#2dd4bf). Distinct from all existing cert colors +
  // corporate palette; matches the "premium acceleration" feel.
  accelerated: "text-teal-400",
  // Phase PP-15 (Feb 2026) — PDC Trainer cert badge color. teal-300 (#5eead4)
  // is one step lighter than `accelerated`'s teal-400 so PDC Trainer stays
  // visually related to the coaching/training theme without collision. Not
  // used by any other cert or by the schedule-status pill palette.
  pdc_trainer: "text-teal-300",
};
// Brown hex for the Cayenne Off-Road cert — used for dots/borders/accents on the board.
export const CAYENNE_OFFROAD_HEX = "#a0651e";
// Corporate color palette (Phase 15l, Jul 2026) — mirrors backend/domain.py:CORPORATE_COLORS.
// 9 colors total: 8 for driver groups (`CORPORATE_COLOR_ORDER` — auto-assign rotation),
// 1 non-driver lanyard color (`CORPORATE_NON_DRIVER_COLOR = "Red"`) UI-locked to groups
// with `guest_count === 0` (spouses, spectators). Keep this in sync with backend.
export const CORPORATE_COLOR_HEX = {
  Orange:   "#f97316",
  Lime:     "#a3e635",
  Sapphire: "#0891b2",
  Black:    "#0a0a0a",
  White:    "#f5f5f5",
  Miami:    "#5faee3",
  Purple:   "#a855f7",
  Grey:     "#71717a",
  Red:      "#ef4444",
};
// Rotation order for the auto-assign engine and the UI picker for DRIVER groups.
// Red is intentionally excluded — see CORPORATE_NON_DRIVER_COLOR below.
export const CORPORATE_COLOR_ORDER = ["Orange", "Lime", "Sapphire", "Black", "White", "Miami", "Purple", "Grey"];
export const CORPORATE_DRIVER_COLORS = CORPORATE_COLOR_ORDER;
export const CORPORATE_NON_DRIVER_COLOR = "Red";
// Corporate briefing runs 30 min BEFORE the session's drive-time (`s.start` is
// the drive start). Mirrors `backend/domain.py::CORPORATE_BRIEFING_MINUTES` and
// the `minusMinutes(s.start, 30)` calls in `printouts.js`. NEVER include this
// window when computing rotation timings — see `lib/rotation.js`.
export const CORPORATE_BRIEFING_MINUTES = 30;
export const minusMinutes = (hhmm, mins) => {
  const [h, m] = (hhmm || "0:0").split(":").map(Number);
  let t = h * 60 + m - mins;
  if (t < 0) t += 24 * 60;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};
// Fleet-car status enum (Feb 2026 — normalized to 4 values). Wire values are snake_case;
// STATUS_LABELS holds the uppercase-with-space display strings. Only `available` cars are
// eligible for session assignment (auto-assign + manual picker enforce this).
export const STATUS_COLORS = {
  available:    "border-emerald-700 text-emerald-400 bg-emerald-950/30",
  on_display:   "border-blue-700    text-blue-400    bg-blue-950/30",
  workshop:     "border-orange-700  text-orange-400  bg-orange-950/30",
  out_of_order: "border-red-800     text-red-400     bg-red-950/30",
};
export const STATUS_LABELS = {
  available:    "AVAILABLE",
  on_display:   "ON DISPLAY",
  workshop:     "WORKSHOP",
  out_of_order: "OUT OF ORDER",
};
export const STATUS_ORDER = ["available", "on_display", "workshop", "out_of_order"];
export const TRACK_LABELS = { south: "South Track", west: "West Track" };
export const CORP_TYPE_ORDER = ["conference", "boardroom", "event_space", "dining", "tour", "delivery", "admission", "other"];
export const CORP_TYPE_LABELS = { conference: "Conference Room", boardroom: "Boardroom", event_space: "Event Space", dining: "Dining", tour: "Corporate Tour", delivery: "New Vehicle Delivery", admission: "General Admission", other: "Other" };
// Phase NH1 (Feb 2026 · Prod cutover follow-up) — "New Hires" is a
// UX partition, NOT a new `employment_type` value. Coaches carry an
// orthogonal `is_new_hire: bool` flag; when true they render under
// this synthetic group instead of their `employment_type` bucket.
// Auto-assign engine ignores the flag by design in v1.
export const EMPLOYMENT_ORDER = ["lead", "new_hire", "full_time", "part_time"];
export const EMPLOYMENT_LABELS = { lead: "Leads", new_hire: "New Hires", full_time: "Full Time", part_time: "Part Time" };

// Senior-instructor rank — orthogonal to TIER and EMPLOYMENT. null/missing = unranked
// (the default; these coaches are scheduled first so seniors are spared). Ops rule (Feb 2026):
// the auto-assign candidate pool EXCLUDES ACI / CI / NVDM entirely — those ranks are
// manual-picker-only in Session Editor. SLI remains in the auto-assign pool as the last-resort
// fallback. Prestige palette (cyan/indigo/gold/teal) — teal reserved for NVDM (Phase 6.2
// NVD Manager rank) so it stays visually distinct from tier1's green.
export const LEAD_RANK_ORDER = ["SLI", "ACI", "CI", "NVDM"];
export const LEAD_RANK_LABELS = {
  SLI: "SLI",
  ACI: "ACI",
  CI: "CI",
  NVDM: "NVDM",
};
export const LEAD_RANK_FULL_LABELS = {
  SLI: "Senior Lead Instructor",
  ACI: "Assistant Chief Instructor",
  CI: "Chief Instructor",
  NVDM: "NVD Manager",
};
export const LEAD_RANK_COLORS = {
  SLI: "bg-cyan-950/40 text-cyan-300 border-cyan-700",
  ACI: "bg-indigo-950/40 text-indigo-300 border-indigo-700",
  CI: "bg-amber-900/40 text-amber-200 border-amber-500",
  NVDM: "bg-teal-950/40 text-teal-300 border-teal-600",
};
export const groupByEmployment = (list) =>
  EMPLOYMENT_ORDER.map((key) => ({
    key,
    label: EMPLOYMENT_LABELS[key],
    coaches: list.filter((c) => {
      // NH1 — is_new_hire wins over employment_type for grouping so a
      // new hire doesn't get double-listed (or hidden from ops in a
      // pay-bucket the operator wouldn't check first). Non-new-hires
      // fall back to their normal employment_type bucket.
      if (key === "new_hire") return !!c.is_new_hire;
      if (c.is_new_hire) return false;
      return (c.employment_type || "part_time") === key;
    }),
  })).filter((g) => g.coaches.length > 0);
export const DEMO_TYPE_LABELS = { gt: "GT", gt_drift: "GT Drift", retail: "Retail", west_gt: "West GT" };
// Phase G1.5 (Feb 2026) — corp-flavored labels for the same demo_type
// keys. Rendered ONLY on sessions with `is_corporate === true` (via
// gating in DemoCardBody). Retail sibling sessions on the same slot
// (post-G1.4 split) continue to render the plain `DEMO_TYPE_LABELS`,
// so ops still gets clear visual separation on mixed slots.
export const DEMO_TYPE_LABELS_CORP = { gt: "Corp", gt_drift: "Corp Drift", retail: "Retail", west_gt: "West Corp" };
// Demo-type colors — mirror the coach-editor certification colors (CERT_COLORS) so a GT demo
// reads the same green on the board as the GT Demo cert chip on a coach card.
export const DEMO_TYPE_COLORS = {
  gt: { text: "text-lime-400", border: "border-lime-700", hex: "#a3e635" },        // GT → green
  retail: { text: "text-white", border: "border-zinc-500", hex: "#ffffff" },        // Retail → white
  gt_drift: { text: "text-orange-400", border: "border-orange-700", hex: "#fb923c" }, // GT Drift → orange
  west_gt: { text: "text-purple-400", border: "border-purple-700", hex: "#c084fc" }, // West GT → purple
};
// Phase G1.14 (Feb 2026) — corp-flavor palette override. Every corp-
// labeled count pill (top of tile) + per-driver corp chip (expanded
// body) uses this map instead of `DEMO_TYPE_COLORS` so corp entries
// read as a unified purple across all demo types, visually distinct
// from the underlying-type-flavored retail pills. Filled `bg-purple-*`
// background (retail pills are outline-only) doubles the separation:
// even when a corp pill sits next to a `west_gt` retail pill (whose
// existing outline is `text-purple-400`), the filled corp bg reads as
// a distinct affordance. Kept a shade LIGHTER than the fuchsia-900
// "Corporate Demo" chip (G1.5) so the two never visually blur.
//
// Same shape/keys as `DEMO_TYPE_COLORS` — the consumer swap in
// `DemoCardBody.jsx` is a one-line dictionary lookup change; no
// downstream class-name plumbing changes.
export const DEMO_TYPE_COLORS_CORP = {
  gt:       { text: "text-purple-200", border: "border-purple-400/60", bg: "bg-purple-500/20", hex: "#a855f7" },
  gt_drift: { text: "text-purple-200", border: "border-purple-400/60", bg: "bg-purple-500/20", hex: "#a855f7" },
  retail:   { text: "text-purple-200", border: "border-purple-400/60", bg: "bg-purple-500/20", hex: "#a855f7" },
  west_gt:  { text: "text-purple-200", border: "border-purple-400/60", bg: "bg-purple-500/20", hex: "#a855f7" },
};
export const OP_TYPE_LABELS = { taste_of_porsche: "Taste of Porsche", short_shift: "Short Shift", pdc_training: "PDC Training", shuttle: "Shuttle", cayenne_offroad: "Cayenne Off-Road" };
// Display-only duration hint for operational rows — surfaced in dropdown pickers,
// operations panels, toolbar tooltips, and cell hover-cards. NOT surfaced in
// manifest print/XLSX (locked to reference ops format) or in `op_type` DB tokens.
export const OP_TYPE_DURATIONS = { taste_of_porsche: "15 min", short_shift: "45 min" };
export const OP_TYPE_ORDER = ["taste_of_porsche", "short_shift", "pdc_training", "shuttle", "cayenne_offroad"];
export const OP_CAR_RULE_LABELS = { taste_of_porsche: "Same car all day", short_shift: "Same car all day", pdc_training: "718 Cayman (GTS or lower)", shuttle: "4-door shuttle vehicle", cayenne_offroad: "Cayenne · requires Cayenne Off-Road cert" };
export const HOME_TRACK_LABELS = { south: "South Track", west: "West Track", both: "South/West Track" };

export const fmtTime = (hhmm) => {
  if (!hhmm || !hhmm.includes(":")) return hhmm || "";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${period}`;
};

const GROUP_PALETTE = ["#60a5fa", "#f472b6", "#34d399", "#fbbf24", "#a78bfa", "#fb923c", "#22d3ee", "#f87171", "#a3e635", "#e879f9"];
export const groupColor = (name) => {
  if (!name) return null;
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return GROUP_PALETTE[h % GROUP_PALETTE.length];
};
